import type * as React from 'react'
import { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'
import { CellProps, Column } from 'react-table'
import { useMediaQuery } from '@react-hookz/web'
import clsx from 'clsx'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { FormatDate } from 'Components/FormatDate/FormatDate'
import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'
import { OrderStatusBadge } from 'Components/OrderStatusBadge/OrderStatusBadge'
import { TableList } from 'Components/TableList/TableList'

import { useIsOrganisation } from 'Hooks'

import { buttonize, getFullContactName, getOrderDisplayName } from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'
import { E2E_TABLE_DEVICE_NAME, E2E_TABLE_LINK_ORDERS } from 'Constants/e2e'

import { ImagePlaceholderTypesEnum } from 'Types/imagePlaceholders.type'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import {
  EmployeeOrdersWithUrl,
  OrdersTableProps,
  OrganisationOrdersWithUrl,
} from './OrdersTable.types'

export const OrdersTable = (props: OrdersTableProps) => {
  const {
    orders,
    isActiveEmployee,
    isLoading,
    isSearching,
    onOpenPreviewColumn,
    selectedRowIndex,
  } = props
  const { employeeId, organisationId } = useParams<{
    employeeId: string
    organisationId: string
  }>()
  const isOrganisation = useIsOrganisation()

  const routerHistory = useHistory()
  const isTablet = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.LG})`)

  const handleEnterDetails = useCallback(
    (
      _event:
        | React.MouseEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLDivElement>,
      id: string,
      url: string,
      rowIndex: number,
    ) => {
      if (!isTablet) {
        routerHistory.push(url)

        return
      }

      if (typeof onOpenPreviewColumn === 'function') {
        onOpenPreviewColumn(id, rowIndex)
      }
    },
    [isTablet, onOpenPreviewColumn, routerHistory],
  )

  const renderOrdersHeader = useCallback(
    () => <FormattedMessage id="OrderListContent_header__title" />,
    [],
  )

  const renderEmployeeHeader = useCallback(
    () => <FormattedMessage id="RequestListContent_header__employee" />,
    [],
  )

  const renderDateHeader = useCallback(
    () => <FormattedMessage id="RequestListContent_header__date" />,
    [],
  )

  const renderStatusHeader = useCallback(
    () => <FormattedMessage id="RequestListContent_header__status" />,
    [],
  )

  const renderOrderCell = useCallback(
    (
      cell: React.PropsWithChildren<
        CellProps<EmployeeOrdersWithUrl | OrganisationOrdersWithUrl>
      >,
    ) => {
      const order = cell.row.original

      const getRequestedItemName = () => {
        if (order.data.requestedDevice) {
          return order.data.requestedDevice.name ?? ''
        }

        if (order.data.requestedRentCategory?.deviceDefinition?.manufacturer) {
          return `${order.data.requestedRentCategory.deviceDefinition.manufacturer} ${order.data.requestedRentCategory.deviceDefinition.codename}`
        }

        if (order.data.requestedRentCategory?.deviceDefinition?.productType) {
          return order.data.requestedRentCategory.deviceDefinition.productType
        }

        return ''
      }

      const displayName = getOrderDisplayName(
        order.caseType,
        getRequestedItemName(),
      )

      return (
        <div
          className="d-inline-flex"
          {...buttonize(
            handleEnterDetails,
            order.id,
            order.url,
            cell.row.index,
          )}
        >
          <div className="d-flex justify-content-center align-items-center me-4 Table-device-image">
            <ImageWithFallback
              alt={
                order.data.requestedRentCategory?.deviceDefinition?.name ||
                'New question icon'
              }
              placeholderType={ImagePlaceholderTypesEnum.requests}
              src={
                order.data.requestedRentCategory?.deviceDefinition?.imageURL ||
                ''
              }
            />
          </div>

          <div className="d-flex flex-column justify-content-center text-ellipsis">
            <div className="d-flex align-items-center">
              <strong
                className="text-ellipsis"
                data-e2e={E2E_TABLE_DEVICE_NAME}
              >
                {typeof displayName === 'string' ? (
                  <FormattedMessage id={displayName} />
                ) : (
                  <>
                    <FormattedMessage id={displayName.type} />:{' '}
                    {displayName.name}
                  </>
                )}
              </strong>
            </div>

            <div className="d-flex table-text-subtitle text-muted">
              <span>#</span>

              <span className="text-ellipsis" data-orderid={order.id}>
                {order.id}
              </span>
            </div>
          </div>
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderExpandableContent = useCallback(
    (
      cell: React.PropsWithChildren<
        CellProps<EmployeeOrdersWithUrl | OrganisationOrdersWithUrl>
      >,
    ) => {
      const order = cell.row.original

      return (
        <div className="w-100 Table-details">
          {!isActiveEmployee && (
            <div className="Table-details-row">
              <span className="Table-row--expandable-title">
                {renderEmployeeHeader()}
              </span>

              <span className="text-ellipsis">
                {order.assignedEmployee?.contact?.firstName
                  ? getFullContactName(order.assignedEmployee.contact)
                  : '-'}
              </span>
            </div>
          )}

          <div className="Table-details-row">
            <span className="Table-row--expandable-title">
              {renderDateHeader()}
            </span>

            <span>
              <FormatDate value={order.created} />
            </span>
          </div>

          <div className="Table-details-row">
            <span className="Table-row--expandable-title">
              {renderStatusHeader()}
            </span>

            <span>
              <OrderStatusBadge status={order.status} />
            </span>
          </div>
        </div>
      )
    },
    [
      isActiveEmployee,
      renderDateHeader,
      renderEmployeeHeader,
      renderStatusHeader,
    ],
  )

  const renderEmployeeCell = useCallback(
    (
      cell: React.PropsWithChildren<
        CellProps<EmployeeOrdersWithUrl | OrganisationOrdersWithUrl>
      >,
    ) => {
      const order = cell.row.original

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            order.id,
            order.url,
            cell.row.index,
          )}
        >
          <span className="text-ellipsis">
            {order.assignedEmployee?.contact?.firstName
              ? getFullContactName(order.assignedEmployee.contact)
              : '-'}
          </span>
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderDateCell = useCallback(
    (
      cell: React.PropsWithChildren<
        CellProps<EmployeeOrdersWithUrl | OrganisationOrdersWithUrl>
      >,
    ) => {
      const order = cell.row.original

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            order.id,
            order.url,
            cell.row.index,
          )}
        >
          <FormatDate value={order.created} />
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderStatusCell = useCallback(
    (
      cell: React.PropsWithChildren<
        CellProps<EmployeeOrdersWithUrl | OrganisationOrdersWithUrl>
      >,
    ) => {
      const order = cell.row.original

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            order.id,
            order.url,
            cell.row.index,
          )}
        >
          <OrderStatusBadge status={order.status} />
        </div>
      )
    },
    [handleEnterDetails],
  )

  const columns = useMemo((): Column<
    EmployeeOrdersWithUrl | OrganisationOrdersWithUrl
  >[] => {
    const tableColumns = [
      {
        Cell: renderOrderCell,
        ExpandableContent: renderExpandableContent,
        Header: renderOrdersHeader,
        id: 'order',
      },
      {
        Cell: renderEmployeeCell,
        Header: renderEmployeeHeader,
        id: 'employee',
      },
      {
        Cell: renderDateCell,
        Header: renderDateHeader,
        id: 'date',
      },
      {
        Cell: renderStatusCell,
        Header: renderStatusHeader,
        id: 'status',
      },
    ]

    return tableColumns.filter(column => {
      // don't show employee when logged in as employee - they are the same
      if (isActiveEmployee && column.id === 'employee') {
        return false
      }

      return true
    })
  }, [
    isActiveEmployee,
    renderDateCell,
    renderDateHeader,
    renderEmployeeCell,
    renderEmployeeHeader,
    renderExpandableContent,
    renderOrderCell,
    renderOrdersHeader,
    renderStatusCell,
    renderStatusHeader,
  ])

  const data = useMemo(
    (): EmployeeOrdersWithUrl[] | OrganisationOrdersWithUrl[] =>
      orders.map(order => {
        const url = composePath({
          isOrganisation,
          params: {
            employeeId,
            orderId: order.id,
            organisationId,
          },
          paths: {
            employee: GLOBAL_ROUTE_PATHS.employee.order,
            organisation: GLOBAL_ROUTE_PATHS.organisation.order,
          },
        })

        return {
          ...order,
          url,
        }
      }),
    [employeeId, isOrganisation, orders, organisationId],
  )

  return (
    <TableList
      className={clsx(
        'Table--orders',
        isOrganisation ? 'Table--isAdmin' : 'Table--isEmployee',
      )}
      columns={columns}
      data={data}
      e2eRowId={E2E_TABLE_LINK_ORDERS}
      isLoading={isLoading}
      isSearching={isSearching}
      selectedRowIndex={selectedRowIndex}
    />
  )
}
